class HeroFullscreenComponent extends HTMLElement {
    constructor() {
        super();

        this.slides = [];
        this.currentSlide = 0;
        this.autoplayInterval = null;
        this.isPlaying = true;
        this.videoEndedBound = this.onVideoEnded.bind(this);
        this._isFrozen = false;
        this._pendingVideoStartCleanup = null;
        this._viewportMedia = null;
        this._viewportChangeBound = null;
        this.initialBufferSeconds = 0.5;
        this.videoStartTimeout = 2000;
    }

    connectedCallback() {
        this.init();
    }

    disconnectedCallback() {
        this.cleanup();
    }

    init() {
        this.slidesContainer = this.querySelector('.hero-fullscreen__slides');
        this.allSlideElements = Array.from(this.querySelectorAll('.hero-fullscreen__slide'));
        this.spacer = this.parentElement.querySelector('.hero-fullscreen__spacer');

        this.autoRotate = this.dataset.autoRotate === 'true';
        this.autoplaySpeed = Number.parseInt(this.dataset.speed, 10) * 1000 || 5000;
        this.enableVideoLooping = this.dataset.videoLooping === 'true';

        // Apply skip-after-watch: hide video slides if user has already seen them
        this.applySkipAfterWatch();

        this.slides = Array.from(this.querySelectorAll('.hero-fullscreen__slide:not([data-hidden])'));
        this.dots = Array.from(this.querySelectorAll('.hero-fullscreen__dot'));
        this.counterCurrent = this.querySelector('.hero-fullscreen__counter-current');
        this.scrollIndicator = this.querySelector('.hero-fullscreen__scroll-indicator');

        if (this.slides.length === 0) return;

        this.showSlide(0);
        this.bindEvents();

        if (this.autoRotate && this.slides.length > 1) {
            this.startAutoplay();
        }

        this.setupScrollObserver();
    }

    // =========================================================
    // SKIP AFTER WATCH
    // =========================================================

    applySkipAfterWatch() {
        if (this.dataset.skipAfterWatch !== 'true') return;
        if (localStorage.getItem('hero_video_watched') !== '1') return;

        // Hide video slides that are NOT exempt from the skip rule
        this.allSlideElements.forEach((slide) => {
            if (slide.dataset.slideType === 'video' && slide.dataset.exemptSkip !== 'true') {
                slide.style.display = 'none';
                slide.setAttribute('data-hidden', 'true');
                slide.setAttribute('aria-hidden', 'true');

                // Completely remove the videos from the DOM so they cannot play anything
                const videos = slide.querySelectorAll('video');
                videos.forEach(video => {
                    video.muted = true;
                    video.volume = 0;
                    video.pause();
                    video.removeAttribute('src'); // Stop downloading
                    video.load(); // Apply src removal
                    video.remove(); // Remove element
                });
            }
        });

        // Hide dots for hidden slides
        const dots = Array.from(this.querySelectorAll('.hero-fullscreen__dot'));
        dots.forEach((dot, index) => {
            const targetId = dot.getAttribute('aria-controls');
            const targetSlide = targetId ? document.getElementById(targetId) : null;
            if (targetSlide && targetSlide.dataset.hidden === 'true') {
                dot.style.display = 'none';
            }
        });
    }

    markVideoWatched() {
        localStorage.setItem('hero_video_watched', '1');
    }

    // =========================================================
    // EVENTS
    // =========================================================

    bindEvents() {
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToSlide(index));
        });

        this.prevArrow = this.querySelector('.hero-fullscreen__arrow--prev');
        this.nextArrow = this.querySelector('.hero-fullscreen__arrow--next');

        if (this.prevArrow) {
            this.prevArrow.addEventListener('click', () => {
                this.prevSlide();
                this.resetAutoplay();
            });
        }

        if (this.nextArrow) {
            this.nextArrow.addEventListener('click', () => {
                this.nextSlide();
                this.resetAutoplay();
            });
        }

        if (this.scrollIndicator) {
            this.scrollIndicator.addEventListener('click', () => this.scrollToContent());
        }

        this.slides.forEach((slide, index) => {
            const videos = slide.querySelectorAll('video');
            videos.forEach((video) => {
                video.addEventListener('ended', this.videoEndedBound);
                video.addEventListener('play', () => this.onVideoPlay(index, slide, video));
                video.addEventListener('pause', () => slide.classList.remove('is-playing'));
                video.addEventListener('ended', () => slide.classList.remove('is-playing'));
            });

            const muteBtn = slide.querySelector('.hero-fullscreen__mute-btn');
            if (muteBtn) {
                muteBtn.addEventListener('click', (e) => this.toggleMute(e, slide));
            }

            const playBtn = slide.querySelector('.hero-fullscreen__play-btn');
            if (playBtn) {
                playBtn.addEventListener('click', (e) => this.onPlayButtonClick(e, slide));
            }

            this.setupCustomPlayerControls(slide);
        });

        this.addEventListener('mouseenter', () => this.pauseAutoplay());
        this.addEventListener('mouseleave', () => {
            if (this.isPlaying) this.startAutoplay();
        });
        this.addEventListener('focusin', () => this.pauseAutoplay());
        this.addEventListener('focusout', () => {
            if (this.isPlaying) this.startAutoplay();
        });

        this.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') this.prevSlide();
            if (e.key === 'ArrowRight') this.nextSlide();
        });

        this._viewportMedia = window.matchMedia('(max-width: 749px)');
        this._viewportChangeBound = () => {
            const slide = this.slides[this.currentSlide];
            if (!slide || slide.dataset.slideType !== 'video') return;

            this.pauseAllVideos();
            this.playCurrentSlideVideo();
        };

        if (this._viewportMedia.addEventListener) {
            this._viewportMedia.addEventListener('change', this._viewportChangeBound);
        } else {
            this._viewportMedia.addListener(this._viewportChangeBound);
        }
    }

    // =========================================================
    // SCROLL OBSERVER — freeze/resume carousel when out of view
    // =========================================================

    setupScrollObserver() {
        this._hasScrolledPast = false;

        const isHeroOutOfView = () => {
            // Use getBoundingClientRect for reliable cross-device detection
            const rect = this.getBoundingClientRect();
            return rect.bottom <= 50;
        };

        const onScroll = () => {
            const outOfView = isHeroOutOfView();

            if (outOfView && !this._hasScrolledPast) {
                // Hero scrolled out of view — freeze IMMEDIATELY
                this._hasScrolledPast = true;
                document.body.classList.add('scrolled-past-hero');
                this._isFrozen = true;
                this.freezeHero();

            } else if (!outOfView && this._hasScrolledPast) {
                // Hero back in view — resume
                this._hasScrolledPast = false;
                document.body.classList.remove('scrolled-past-hero');
                this.resumeHero();
            }
        };

        // Fluidez: no maximo 1 leitura de retangulo por quadro
        let queued = false;
        const onScrollQueued = () => {
            if (queued) return;
            queued = true;
            requestAnimationFrame(() => { queued = false; onScroll(); });
        };
        window.addEventListener('scroll', onScrollQueued, { passive: true });

        this._scrollObserverCleanup = () => {
            window.removeEventListener('scroll', onScrollQueued);
        };

        this.setupScrollSnap();
    }

    freezeHero() {
        this._isFrozen = true;
        this.pauseAutoplay();
        this.pauseAllVideos();
    }

    resumeHero() {
        if (!this._isFrozen) return;

        this._isFrozen = false;

        this.playCurrentSlideVideo();

        if (this.autoRotate && this.slides.length > 1) {
            this.startAutoplay();
        }
    }

    // =========================================================
    // SCROLL SNAP — legacy, with iOS flicker fix
    // =========================================================

    setupScrollSnap() {
        if (window.location.pathname !== '/' && !document.body.classList.contains('template-index')) {
            return;
        }

        // Respect the global Shopify theme setting. The legacy hero snap must
        // never turn itself on when "Ativar Scroll Snap" is disabled.
        if (document.documentElement.dataset.scrollSnapEnabled !== 'true') {
            return;
        }

        // If global CSS scroll-snap is active, don't activate manual snap
        if (document.documentElement.classList.contains('scroll-snap-active')) {
            return;
        }

        // If global system loads later, clear manual listeners
        document.addEventListener('scrollsnap:active', () => {
            if (this._scrollSnapCleanup) {
                this._scrollSnapCleanup();
                this._scrollSnapCleanup = null;
            }
        }, { once: true });

        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        let isSnapping = false;
        const SNAP_COOLDOWN = 800;

        const getSnapPosition = () => this.offsetHeight;

        const isPopupOpen = () => {
            return document.body.classList.contains('overflow-hidden') ||
                document.body.classList.contains('body-no-scroll') ||
                document.documentElement.classList.contains('overflow-hidden');
        };

        const isInsideScrollablePopup = (target) => {
            return target.closest('cart-theoric') ||
                target.closest('.cart-drawer') ||
                target.closest('#CartTheoric') ||
                target.closest('#cart-items-tab') ||
                target.closest('.drawer__inner') ||
                target.closest('modal-dialog') ||
                target.closest('.modal') ||
                target.closest('[data-modal]');
        };

        if (isTouchDevice) {
            let scrollEndTimer = null;
            let isTouching = false;

            const handleScrollEnd = () => {
                // Don't snap while user finger is still on screen (prevents iOS flicker)
                if (isPopupOpen() || isSnapping || isTouching) return;

                const scrollY = window.scrollY;
                const snapPosition = getSnapPosition();

                if (scrollY > 20 && scrollY < snapPosition - 50) {
                    isSnapping = true;

                    // Disable smooth scroll-behavior temporarily to avoid double-animation on iOS
                    const htmlEl = document.documentElement;
                    const prevBehavior = htmlEl.style.scrollBehavior;
                    htmlEl.style.scrollBehavior = 'auto';

                    const target = scrollY < snapPosition / 2 ? 0 : snapPosition;
                    window.scrollTo({ top: target, behavior: 'smooth' });

                    setTimeout(() => {
                        htmlEl.style.scrollBehavior = prevBehavior;
                        isSnapping = false;
                    }, SNAP_COOLDOWN);
                }
            };

            const onTouchStart = () => { isTouching = true; };
            const onTouchEnd = () => {
                isTouching = false;
                // Run handleScrollEnd after touch ends, slightly delayed
                if (scrollEndTimer) clearTimeout(scrollEndTimer);
                scrollEndTimer = setTimeout(handleScrollEnd, 200);
            };
            const onScroll = () => {
                if (isTouching) return; // Don't snap while touching
                if (scrollEndTimer) clearTimeout(scrollEndTimer);
                scrollEndTimer = setTimeout(handleScrollEnd, 150);
            };

            window.addEventListener('touchstart', onTouchStart, { passive: true });
            window.addEventListener('touchend', onTouchEnd, { passive: true });
            window.addEventListener('scroll', onScroll, { passive: true });

            this._scrollSnapCleanup = () => {
                window.removeEventListener('touchstart', onTouchStart);
                window.removeEventListener('touchend', onTouchEnd);
                window.removeEventListener('scroll', onScroll);
                if (scrollEndTimer) clearTimeout(scrollEndTimer);
            };
            return;
        }

        // Desktop: wheel / keyboard snap
        const handleWheel = (e) => {
            if (isPopupOpen() || isInsideScrollablePopup(e.target)) return;

            if (isSnapping) {
                e.preventDefault();
                return;
            }

            const scrollY = window.scrollY;
            const snapPosition = getSnapPosition();

            if (scrollY <= 20 && e.deltaY > 0) {
                e.preventDefault();
                isSnapping = true;
                window.scrollTo({ top: snapPosition, behavior: 'smooth' });
                setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN);
                return;
            }

            if (scrollY > snapPosition - 100 && scrollY < snapPosition + 200 && e.deltaY < 0) {
                e.preventDefault();
                isSnapping = true;
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN);
                return;
            }

            const isInHeroZone = scrollY > 20 && scrollY < snapPosition - 50;
            if (isInHeroZone) {
                e.preventDefault();
                isSnapping = true;
                if (e.deltaY > 0) {
                    window.scrollTo({ top: snapPosition, behavior: 'smooth' });
                } else {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN);
                return;
            }
        };

        const handleKeydown = (e) => {
            if (isPopupOpen() || isSnapping) return;
            if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'PageDown' && e.key !== 'PageUp') return;

            const scrollY = window.scrollY;
            const snapPosition = getSnapPosition();

            if (scrollY <= 20 && (e.key === 'ArrowDown' || e.key === 'PageDown')) {
                e.preventDefault();
                isSnapping = true;
                window.scrollTo({ top: snapPosition, behavior: 'smooth' });
                setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN);
            }

            if (scrollY > snapPosition - 100 && scrollY < snapPosition + 200 && (e.key === 'ArrowUp' || e.key === 'PageUp')) {
                e.preventDefault();
                isSnapping = true;
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => { isSnapping = false; }, SNAP_COOLDOWN);
            }
        };

        window.addEventListener('wheel', handleWheel, { passive: false });
        window.addEventListener('keydown', handleKeydown, { passive: false });

        this._scrollSnapCleanup = () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('keydown', handleKeydown);
        };
    }

    // =========================================================
    // SLIDE MANAGEMENT
    // =========================================================

    showSlide(index) {
        if (index < 0 || index >= this.slides.length) return;

        if (this._isFrozen) return;

        this.pauseAllVideos();

        const previouslyActive = new Set();
        this.slides.forEach((slide, i) => {
            if (slide.classList.contains('is-active')) previouslyActive.add(i);
        });

        this.slides.forEach((slide, i) => {
            const isActive = i === index;
            slide.classList.toggle('is-transitioning', !isActive && previouslyActive.has(i));
            slide.classList.toggle('is-active', isActive);
            slide.setAttribute('aria-hidden', !isActive);
        });

        this.dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
            dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });

        if (this.counterCurrent) {
            this.counterCurrent.textContent = index + 1;
        }

        this.currentSlide = index;

        this.playCurrentSlideVideo();

        this.dispatchEvent(new CustomEvent('slideChange', {
            detail: { currentSlide: index, totalSlides: this.slides.length }
        }));
    }

    playCurrentSlideVideo() {
        const slide = this.slides[this.currentSlide];
        if (!slide) return;

        this.loadSlideMedia(slide);

        const videos = slide.querySelectorAll('video');
        let visibleVideo = null;

        videos.forEach(video => {
            const isVisible = window.getComputedStyle(video).display !== 'none';
            if (isVisible) {
                visibleVideo = video;
            } else {
                video.muted = true;
                video.volume = 0;
                video.pause();
            }
        });

        if (!visibleVideo) return;

        const shouldAutoplay = visibleVideo.dataset.videoAutoplay === 'true';
        if (!shouldAutoplay) return;

        const shouldBeMuted = visibleVideo.dataset.videoMuted === 'true';

        const attemptPlay = () => {
            if (visibleVideo.readyState > 0) {
                visibleVideo.currentTime = 0;
            }

            // Regra de Ouro do Autoplay em Mobile/Safari:
            // SEMPRE dar o play() com o vídeo mutado. Se tentar desmutar antes do play,
            // o iOS suspende o elemento permanentemente.
            visibleVideo.muted = true;
            visibleVideo.volume = 0;

            const playPromise = visibleVideo.play();
            if (playPromise !== undefined) {
                playPromise.then(() => {
                    // O Play (mudo) funcionou!
                    slide.classList.add('is-playing');
                    const playBtn = slide.querySelector('.hero-fullscreen__play-btn');
                    if (playBtn) playBtn.classList.add('is-hidden');
                    // Agora, se o lojista configurou para tocar COM SOM:
                    if (!shouldBeMuted) {
                        try {
                            // Tenta desmutar
                            visibleVideo.muted = false;
                            this.fadeInVolume(visibleVideo);
                        } catch (e) {
                            // Se o navegador bloquear o "desmute" automático, volta pra mudo
                            visibleVideo.muted = true;
                            const muteBtn = slide.querySelector('.hero-fullscreen__mute-btn');
                            if (muteBtn) {
                                muteBtn.classList.add('is-muted');
                                muteBtn.setAttribute('aria-label', 'Ativar som');
                            }
                        }
                    } else {
                        // Era pra ser mudo mesmo
                        const muteBtn = slide.querySelector('.hero-fullscreen__mute-btn');
                        if (muteBtn) {
                            muteBtn.classList.add('is-muted');
                        }
                    }
                }).catch((error) => {
                    // Falhou até o play mutado (ex: modo economia de bateria)
                    console.warn("Autoplay bloqueado pelo navegador:", error);
                    const playBtn = slide.querySelector('.hero-fullscreen__play-btn');
                    if (playBtn) playBtn.classList.remove('is-hidden');
                });
            }
        };

        if (visibleVideo.dataset.waitForReady === 'true') {
            this.waitForSmoothPlayback(visibleVideo, slide, attemptPlay);
        } else {
            attemptPlay();
        }
    }

    goToSlide(index) {
        if (index === this.currentSlide) return;
        this.showSlide(index);
        this.resetAutoplay();
    }

    loadSlideMedia(slide) {
        if (!slide) return;

        slide.querySelectorAll('video').forEach(video => {
            const isVisible = window.getComputedStyle(video).display !== 'none';
            if (!isVisible) return;

            if (video.dataset.mediaLoaded !== 'true') {
                video.preload = video.dataset.waitForReady === 'true' ? 'auto' : 'metadata';
                video.dataset.mediaLoaded = 'true';
                video.dataset.mediaLoadStartedAt = String(Date.now());
                // The first hero video may already be preloading from the HTML.
                // Calling load() again would restart that request on Safari/iOS.
                if (video.networkState === 0) video.load();
            }
        });

        slide.querySelectorAll('source[data-srcset]').forEach(source => {
            source.srcset = source.dataset.srcset;
            source.removeAttribute('data-srcset');
        });

        slide.querySelectorAll('img[data-src]').forEach(img => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');

            if (img.dataset.srcset) {
                img.srcset = img.dataset.srcset;
                img.removeAttribute('data-srcset');
            }
        });
    }

    isReadyForSmoothPlayback(video) {
        if (video.readyState < 3 || !Number.isFinite(video.duration) || video.duration <= 0) return false;

        const requiredBufferEnd = Math.min(video.duration, video.currentTime + this.initialBufferSeconds);

        for (let index = 0; index < video.buffered.length; index += 1) {
            const startsBeforeCurrentTime = video.buffered.start(index) <= video.currentTime;
            const reachesRequiredBuffer = video.buffered.end(index) >= requiredBufferEnd;

            if (startsBeforeCurrentTime && reachesRequiredBuffer) return true;
        }

        return false;
    }

    waitForSmoothPlayback(video, slide, onReady) {
        this.cancelPendingVideoStart();

        const events = ['loadeddata', 'canplay', 'canplaythrough', 'progress'];
        let finished = false;
        let fallbackTimeout = null;

        const cleanup = () => {
            if (finished) return;
            finished = true;
            if (fallbackTimeout) clearTimeout(fallbackTimeout);
            events.forEach(eventName => video.removeEventListener(eventName, checkReadiness));
            video.removeEventListener('error', cleanup);

            if (this._pendingVideoStartCleanup === cleanup) {
                this._pendingVideoStartCleanup = null;
            }
        };

        const startPlayback = () => {
            const isCurrentSlide = slide === this.slides[this.currentSlide];
            const isVisible = window.getComputedStyle(video).display !== 'none';

            if (!isCurrentSlide || !isVisible) {
                cleanup();
                return;
            }

            cleanup();
            onReady();
        };

        const checkReadiness = () => {
            if (!this.isReadyForSmoothPlayback(video)) return;
            startPlayback();
        };

        events.forEach(eventName => video.addEventListener(eventName, checkReadiness));
        video.addEventListener('error', cleanup, { once: true });
        // Safari can omit reliable buffered ranges for HLS. Do not leave the
        // poster stuck forever; play() will keep loading until playback starts.
        fallbackTimeout = setTimeout(startPlayback, this.videoStartTimeout);
        this._pendingVideoStartCleanup = cleanup;
        checkReadiness();
    }

    cancelPendingVideoStart() {
        if (this._pendingVideoStartCleanup) {
            this._pendingVideoStartCleanup();
        }
    }

    nextSlide() {
        const nextIndex = (this.currentSlide + 1) % this.slides.length;
        this.showSlide(nextIndex);
    }

    prevSlide() {
        const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
        this.showSlide(prevIndex);
    }

    // =========================================================
    // AUTOPLAY
    // =========================================================

    startAutoplay() {
        if (this.autoplayInterval) return;
        if (this._isFrozen) return;

        const currentSlideEl = this.slides[this.currentSlide];
        if (!currentSlideEl) return;

        const videos = currentSlideEl.querySelectorAll('video');
        let visibleVideo = null;
        videos.forEach(v => {
            if (window.getComputedStyle(v).display !== 'none') {
                visibleVideo = v;
            }
        });

        if (visibleVideo && !visibleVideo.paused && !visibleVideo.ended) {
            return;
        }

        this.autoplayInterval = setInterval(() => {
            this.nextSlide();
        }, this.autoplaySpeed);
    }

    pauseAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }

    resetAutoplay() {
        this.pauseAutoplay();
        if (this.autoRotate && this.slides.length > 1) {
            this.startAutoplay();
        }
    }

    pauseAllVideos() {
        this.cancelPendingVideoStart();

        this.slides.forEach((slide) => {
            const videos = slide.querySelectorAll('video');
            videos.forEach(video => video.pause());
        });
    }

    // =========================================================
    // VIDEO EVENTS
    // =========================================================

    onVideoPlay(slideIndex, slide, video) {
        if (slideIndex === this.currentSlide) {
            this.pauseAutoplay();
        }
        // Show poster is gone (playing state)
        slide.classList.add('is-playing');
        // Mark as watched
        this.markVideoWatched();
    }

    onVideoEnded(event) {
        const video = event.target;
        const slide = video.closest('.hero-fullscreen__slide');
        const slideIndex = this.slides.indexOf(slide);

        if (slideIndex !== this.currentSlide) return;

        if (this.enableVideoLooping) {
            video.currentTime = 0;
            video.play();
            return;
        }

        // Remove playing state so poster shows again
        slide.classList.remove('is-playing');

        if (this.slides.length > 1) {
            this.nextSlide();
            this.resetAutoplay();
        }
    }

    toggleMute(event, slide) {
        event.preventDefault();
        event.stopPropagation();

        const videos = slide.querySelectorAll('video');
        const muteBtn = slide.querySelector('.hero-fullscreen__mute-btn');

        if (videos.length === 0 || !muteBtn) return;

        const targetMuted = !muteBtn.classList.contains('is-muted');

        // Get the visible video for fade effect
        let visibleVideo = null;
        videos.forEach(video => {
            const isVisible = window.getComputedStyle(video).display !== 'none';
            if (isVisible) visibleVideo = video;
        });

        if (targetMuted) {
            // Muting: fade out volume then mute
            if (visibleVideo) {
                this.fadeOutVolume(visibleVideo, 300);
            } else {
                videos.forEach(video => {
                    video.muted = true;
                    video.volume = 0;
                });
            }
        } else {
            // Unmuting: fade in volume gradually
            if (visibleVideo) {
                this.fadeInVolume(visibleVideo, 1500);
            } else {
                videos.forEach(video => {
                    video.muted = false;
                    video.volume = 1;
                });
            }
        }

        muteBtn.classList.toggle('is-muted', targetMuted);

        const label = targetMuted ? 'Ativar som' : 'Desativar som';
        muteBtn.setAttribute('aria-label', label);

        muteBtn.style.animation = 'none';
    }

    // =========================================================
    // VOLUME FADE HELPERS
    // =========================================================

    fadeInVolume(video, duration = 2000) {
        // Cancel any existing fade on this video
        if (video._volumeFadeInterval) {
            clearInterval(video._volumeFadeInterval);
            video._volumeFadeInterval = null;
        }

        video.volume = 0;
        video.muted = false;

        const steps = 20;
        const stepTime = duration / steps;
        let currentStep = 0;

        video._volumeFadeInterval = setInterval(() => {
            currentStep++;
            video.volume = Math.min(1, currentStep / steps);
            if (currentStep >= steps) {
                clearInterval(video._volumeFadeInterval);
                video._volumeFadeInterval = null;
            }
        }, stepTime);
    }

    fadeOutVolume(video, duration = 300) {
        // Cancel any existing fade on this video
        if (video._volumeFadeInterval) {
            clearInterval(video._volumeFadeInterval);
            video._volumeFadeInterval = null;
        }

        const startVolume = video.volume;
        if (startVolume === 0) {
            video.muted = true;
            return;
        }

        const steps = 10;
        const stepTime = duration / steps;
        let currentStep = 0;

        video._volumeFadeInterval = setInterval(() => {
            currentStep++;
            video.volume = Math.max(0, startVolume * (1 - currentStep / steps));
            if (currentStep >= steps) {
                clearInterval(video._volumeFadeInterval);
                video._volumeFadeInterval = null;
                video.volume = 0;
                video.muted = true;
            }
        }, stepTime);
    }

    onPlayButtonClick(event, slide) {
        event.preventDefault();
        event.stopPropagation();

        const videos = slide.querySelectorAll('video');
        const playBtn = slide.querySelector('.hero-fullscreen__play-btn');
        let visibleVideo = null;

        videos.forEach(video => {
            const isVisible = window.getComputedStyle(video).display !== 'none';
            if (isVisible) visibleVideo = video;
        });

        if (!visibleVideo) return;

        const playVideo = () => {
            visibleVideo.play().then(() => {
                this.markVideoWatched();
                if (playBtn) playBtn.classList.add('is-hidden');
            }).catch(() => {
                if (playBtn) playBtn.classList.remove('is-hidden');
            });
        };

        // Keep play() inside the tap event so iOS preserves the user gesture.
        playVideo();
    }

    scrollToContent() {
        const heroHeight = this.offsetHeight;
        window.scrollTo({
            top: heroHeight,
            behavior: 'smooth'
        });
    }

    // =========================================================
    // CUSTOM PLAYER CONTROLS
    // =========================================================

    setupCustomPlayerControls(slide) {
        const playerOverlay = slide.querySelector('[data-player-overlay]');
        const playerBar = slide.querySelector('[data-player-bar]');
        const mobilePause = slide.querySelector('[data-mobile-pause]');

        if (!playerOverlay && !playerBar && !mobilePause) return;

        const videos = slide.querySelectorAll('video');
        if (videos.length === 0) return;

        const getVisibleVideo = () => {
            let visibleVideo = null;
            videos.forEach(video => {
                const isVisible = window.getComputedStyle(video).display !== 'none';
                if (isVisible) visibleVideo = video;
            });
            return visibleVideo;
        };

        const formatTime = (seconds) => {
            if (Number.isNaN(seconds) || !Number.isFinite(seconds)) return '0:00';
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        const updateProgress = () => {
            const video = getVisibleVideo();
            if (!video || !playerBar) return;

            const progressBar = playerBar.querySelector('[data-progress-bar]');
            const progressHandle = playerBar.querySelector('[data-progress-handle]');
            const timeCurrent = playerBar.querySelector('[data-time-current]');
            const timeDuration = playerBar.querySelector('[data-time-duration]');

            const percent = (video.currentTime / video.duration) * 100 || 0;

            if (progressBar) progressBar.style.width = `${percent}%`;
            if (progressHandle) progressHandle.style.left = `${percent}%`;
            if (timeCurrent) timeCurrent.textContent = formatTime(video.currentTime);
            if (timeDuration) timeDuration.textContent = formatTime(video.duration);
        };

        const updatePlayState = () => {
            const video = getVisibleVideo();
            if (!video) return;

            const isPaused = video.paused;

            if (playerOverlay) {
                playerOverlay.classList.toggle('is-paused', isPaused);
            }
            if (mobilePause) {
                mobilePause.classList.toggle('is-paused', isPaused);
            }
        };

        const togglePlayPause = (e) => {
            e.preventDefault();
            e.stopPropagation();

            const video = getVisibleVideo();
            if (!video) return;

            if (video.paused) {
                video.play().catch(() => { });
            } else {
                video.pause();
            }
        };

        if (playerOverlay) {
            playerOverlay.addEventListener('click', togglePlayPause);
        }

        if (mobilePause) {
            mobilePause.addEventListener('click', togglePlayPause);
        }

        if (playerBar) {
            const progressContainer = playerBar.querySelector('[data-progress-container]');

            if (progressContainer) {
                let isDragging = false;

                const seek = (e) => {
                    const video = getVisibleVideo();
                    if (!video) return;

                    const rect = progressContainer.getBoundingClientRect();
                    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    video.currentTime = percent * video.duration;
                    updateProgress();
                };

                progressContainer.addEventListener('click', (e) => {
                    e.stopPropagation();
                    seek(e);
                });

                progressContainer.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    isDragging = true;
                    progressContainer.classList.add('is-dragging');
                    seek(e);
                });

                document.addEventListener('mousemove', (e) => {
                    if (isDragging) {
                        seek(e);
                    }
                });

                document.addEventListener('mouseup', () => {
                    if (isDragging) {
                        isDragging = false;
                        progressContainer.classList.remove('is-dragging');
                    }
                });

                progressContainer.addEventListener('mouseenter', () => {
                    playerBar.classList.add('is-active');
                });

                progressContainer.addEventListener('mouseleave', () => {
                    if (!isDragging) {
                        playerBar.classList.remove('is-active');
                    }
                });
            }
        }

        videos.forEach(video => {
            video.addEventListener('timeupdate', updateProgress);
            video.addEventListener('loadedmetadata', updateProgress);
            video.addEventListener('play', updatePlayState);
            video.addEventListener('pause', updatePlayState);
            video.addEventListener('ended', updatePlayState);
        });

        setTimeout(() => {
            updateProgress();
            updatePlayState();
        }, 100);
    }

    // =========================================================
    // CLEANUP
    // =========================================================

    cleanup() {
        if (this._freezeTimeout) clearTimeout(this._freezeTimeout);
        this.cancelPendingVideoStart();
        this.pauseAutoplay();
        this.pauseAllVideos();

        if (this._viewportMedia && this._viewportChangeBound) {
            if (this._viewportMedia.removeEventListener) {
                this._viewportMedia.removeEventListener('change', this._viewportChangeBound);
            } else {
                this._viewportMedia.removeListener(this._viewportChangeBound);
            }
        }

        this.slides.forEach((slide) => {
            const videos = slide.querySelectorAll('video');
            videos.forEach(video => {
                video.removeEventListener('ended', this.videoEndedBound);
            });
        });

        if (this._scrollObserverCleanup) {
            this._scrollObserverCleanup();
        }

        if (this._scrollSnapCleanup) {
            this._scrollSnapCleanup();
        }
    }
}

if (!customElements.get('hero-fullscreen-component')) {
    customElements.define('hero-fullscreen-component', HeroFullscreenComponent);
}
